import { useRef } from "react";

import { Box, Button } from "@ezmid/components-library/";
import { faArrowLeft, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";

import LoginFormWrapper from "./LoginFormWrapper";
import { useRequestResetPasswordMutation } from "../../api/reset-password.generated";
import { Form } from "../../components/hookFormComponents/Form";
import { FormInputField } from "../../components/hookFormComponents/FormInputField";
import { makeRoute } from "../../hooks/route";
import { emailRegex } from "../../utils/isEventTriggeredInElement";
import { handleValidationErrors } from "../../utils/validationErrorHandler";
import { AllRoutes } from "../route/Route";

type FormValues = {
  email: string;
};

export default function ResetPasswordView() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const FormSchema = yup.object().shape({
    email: yup
      .string()
      .matches(emailRegex, t("form.email.invalid"))
      .required(t("form.email.required")),
  });

  const {
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    control,
    setError,
  } = useForm<FormValues>({
    defaultValues: {
      email: "",
    },
    mode: "all",
    resolver: yupResolver(FormSchema),
  });
  const [requestResetPassword] = useRequestResetPasswordMutation();

  const submitRef = useRef<HTMLButtonElement>(null);

  const onSubmit = () => {
    const { email } = getValues();
    requestResetPassword({ resetPasswordRequest: { email } })
      .unwrap()
      .then(() => {
        navigate(makeRoute(AllRoutes.AUTH_LOGIN));
        toast.success(t("app.forgot-password-modal.link-sent"));
      })
      .catch((error) => {
        const { hasFieldErrors, errorMessage } = handleValidationErrors(
          error,
          setError
        );
        if (!hasFieldErrors) {
          toast.warning(errorMessage || t("app.forgot-password-modal-failed"));
        }
      });
  };

  return (
    <LoginFormWrapper>
      <h1 className="text-4xl mb-6">{t("app.auth.resetPassword")}</h1>
      <p className="text-gray-400 mb-4">{t("app.auth.enterYourEmail")}</p>
      <Form
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        submitRef={submitRef}
      >
        <FormInputField
          name="email"
          htmlFor="email"
          label="Email"
          id="email"
          type="text"
          control={control}
          error={errors.email}
          placeholder="email@email.com"
        />
      </Form>
      <Box display="flex">
        <Box marginRight="mr-2">
          <Button
            type="button"
            variant="primary"
            leftIcon={<FontAwesomeIcon icon={faPaperPlane} />}
            onClick={() => onSubmit()}
            disabled={!isValid}
          >
            {t("button.send")}
          </Button>
        </Box>

        <Link to={makeRoute(AllRoutes.AUTH_LOGIN)}>
          <Button
            type="button"
            variant="primary"
            leftIcon={<FontAwesomeIcon icon={faArrowLeft} />}
            onClick={() => navigate(makeRoute(AllRoutes.AUTH_LOGIN))}
          >
            {t("button.back")}
          </Button>
        </Link>
      </Box>
    </LoginFormWrapper>
  );
}
